'use client';

import { useQueryClient } from '@tanstack/react-query';
import { PropsWithChildren, useEffect, useState } from 'react';
import { useRouter, useSelectedLayoutSegments } from 'next/navigation';

import AuthContext from './AuthContext';

import { ClientAuth } from '@/shared/auth';
import { PATH } from '@/shared/constants';

interface AuthProviderProps {
  initialIsAuth: boolean;
}

const AuthProvider = ({
  children,
  initialIsAuth,
}: PropsWithChildren<AuthProviderProps>) => {
  const router = useRouter();
  const segments = useSelectedLayoutSegments();
  const queryClient = useQueryClient();

  const [isAuth, setIsAuth] = useState(initialIsAuth);

  useEffect(() => {
    const validAuth = async () => {
      const accessToken = await ClientAuth.getAccessToken();
      setIsAuth(!!accessToken);
    };

    document.addEventListener('visibilitychange', validAuth);

    return () => document.removeEventListener('visibilitychange', validAuth);
  }, []);

  const resetAuth = async () => {
    if (segments.includes('(authorized)') || segments.includes('verify')) {
      return router.push(PATH.HOME);
    }

    router.refresh();
    queryClient.clear();
  };

  useEffect(() => {
    if (!isAuth && initialIsAuth) resetAuth();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuth, segments, initialIsAuth]);

  return (
    <AuthContext.Provider value={{ isAuth }}>{children}</AuthContext.Provider>
  );
};

export default AuthProvider;
